import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Bucket, EmptyRowsMode, MetricsMode, ReportQuery, TeachersMode } from './types';

/** Ten weeks: long enough to show a pattern, short enough to stay readable. */
export const DEFAULT_WINDOW_DAYS = 70;

const VALID_BUCKETS: Bucket[] = ['week', 'month'];

export interface UrlState {
  query: ReportQuery;
  teacherSearch: string;
  /**
   * Admin override for the active-day denominator (see intensity.ts's
   * DAYS_PER_BUCKET). `null` means "use the bucket default". This is
   * deliberately NOT part of `ReportQuery`: it only changes how existing
   * counts are coloured and described, never what is fetched, so it must
   * stay outside `queryKeyFor` in hooks.ts and never trigger a refetch --
   * the same reasoning that keeps `teacherSearch` out of `ReportQuery`.
   */
  activeDayTarget: number | null;
  /** Hides unused activity types. View-only, like `activeDayTarget`: it never refetches. */
  metricsMode: MetricsMode;
  /** Hides rows with no active day. View-only, like `metricsMode`. */
  emptyRows: EmptyRowsMode;
}

const iso = (date: Date): string => date.toISOString().slice(0, 10);

export const defaultQuery = (today: Date): ReportQuery => {
  const end = new Date(today.getTime());
  const start = new Date(today.getTime());
  start.setUTCDate(start.getUTCDate() - DEFAULT_WINDOW_DAYS);
  return {
    start: iso(start),
    end: iso(end),
    bucket: null,
    schoolIds: [],
    curriculumId: null,
    subjectId: null,
    teachersMode: 'active',
    districtId: null,
  };
};

const numberOrNull = (value: string | null): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Highest sane active-day target: more than the days in a bucket-month. */
const MAX_ACTIVE_DAY_TARGET = 31;

/**
 * Parses the `target` override defensively: a non-numeric, zero, negative,
 * or absurdly large (>31) value falls back to `null` ("use the bucket
 * default") rather than throwing or producing a broken colour scale.
 */
const parseActiveDayTarget = (value: string | null): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0 || parsed > MAX_ACTIVE_DAY_TARGET) return null;
  return Math.floor(parsed);
};

/** Old links may still say bucket=day; open them as week. Unknown values use the server default. */
const parseBucket = (value: string | null): Bucket | null => {
  if (value === 'day') return 'week';
  return VALID_BUCKETS.includes(value as Bucket) ? (value as Bucket) : null;
};

/** Old links used include_inactive=true, which meant the same as teachers=all. */
const parseTeachersMode = (params: URLSearchParams): TeachersMode =>
  params.get('teachers') === 'all' || params.get('include_inactive') === 'true'
    ? 'all'
    : 'active';

export const parseUrlState = (search: string, today: Date): UrlState => {
  const params = new URLSearchParams(search);
  const fallback = defaultQuery(today);

  const schoolIds = params
    .getAll('school_ids')
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  return {
    query: {
      start: params.get('start') || fallback.start,
      end: params.get('end') || fallback.end,
      bucket: parseBucket(params.get('bucket')),
      schoolIds,
      curriculumId: numberOrNull(params.get('curriculum_id')),
      subjectId: numberOrNull(params.get('subject_id')),
      teachersMode: parseTeachersMode(params),
      districtId: numberOrNull(params.get('district_id')),
    },
    teacherSearch: params.get('q') || '',
    activeDayTarget: parseActiveDayTarget(params.get('target')),
    metricsMode: params.get('metrics') === 'all' ? 'all' : 'used',
    emptyRows: params.get('zeros') === 'show' ? 'show' : 'hide',
  };
};

export const serialiseUrlState = (state: UrlState): string => {
  const params = new URLSearchParams();
  params.set('start', state.query.start);
  params.set('end', state.query.end);
  if (state.query.bucket) params.set('bucket', state.query.bucket);
  if (state.query.districtId) {
    params.set('district_id', String(state.query.districtId));
  }
  if (state.query.curriculumId) {
    params.set('curriculum_id', String(state.query.curriculumId));
  }
  if (state.query.subjectId) params.set('subject_id', String(state.query.subjectId));
  // Default modes stay out of the URL.
  if (state.query.teachersMode === 'all') params.set('teachers', 'all');
  state.query.schoolIds.forEach((id) => params.append('school_ids', String(id)));
  if (state.teacherSearch) params.set('q', state.teacherSearch);
  if (state.activeDayTarget) params.set('target', String(state.activeDayTarget));
  if (state.metricsMode === 'all') params.set('metrics', 'all');
  if (state.emptyRows === 'show') params.set('zeros', 'show');
  return `?${params.toString()}`;
};

/**
 * Reads state from the URL and writes patches back to it. The URL is the
 * single source of truth, so a refresh or a pasted link reproduces the view
 * exactly.
 */
export const useUrlState = (): [UrlState, (patch: Partial<UrlState>) => void] => {
  const location = useLocation();
  const history = useHistory();

  const state = useMemo(
    () => parseUrlState(location.search, new Date()),
    [location.search],
  );

  const update = useCallback(
    (patch: Partial<UrlState>) => {
      const next: UrlState = {
        ...state,
        ...patch,
        query: { ...state.query, ...(patch.query || {}) },
      };
      history.replace({
        pathname: location.pathname,
        search: serialiseUrlState(next),
      });
    },
    [state, history, location.pathname],
  );

  return [state, update];
};
